import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import quoteService from '../services/quoteService';

const SignInUser = () => {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  // Format phone number to E.164 format
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');

    // Add +1 for US numbers if not present
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }

    // Already has country code
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }

    // Already formatted or has + prefix
    if (value.startsWith('+')) {
      return value;
    }

    return value;
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!phone.trim() || phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = formatPhoneNumber(phone);
      const result = await quoteService.sendOTP(formattedPhone);
      if (result.success) {
        setPhone(formattedPhone);
        setStep('otp');
        toast.success('OTP sent! Check your phone.');
      } else {
        toast.error(result.error || 'Failed to send OTP');
      }
    } catch (err) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp.trim()) {
      toast.error('Please enter the OTP code');
      return;
    }

    setLoading(true);
    try {
      const result = await quoteService.verifyOTP(phone, otp);
      if (result.success) {
        toast.success('Signed in successfully! Welcome back.');
        await loginUser(phone);
        navigate('/user');
      } else {
        toast.error(result.error || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-yellow-50/40 flex items-center justify-center px-4 py-10">
      <Helmet>
        <title>Sign In – MyMoveAdvisor</title>
        <meta name="description" content="Sign in to your MyMoveAdvisor account to manage your moving quotes and track your requests." />
        <link rel="canonical" href="https://mymoveadvisor.com/signin-user" />
      </Helmet>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.08)] border border-gray-200 p-6 md:p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white mb-4 shadow-lg shadow-green-200">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">Sign in as User</h1>
          <p className="text-sm text-gray-500">Enter your phone number to receive an OTP</p>
        </div>

        {step === 'phone' && (
          <form onSubmit={sendOtp} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-orange-300">
                <span className="flex items-center px-3 bg-gray-100 text-gray-600 text-sm font-semibold border-r border-gray-200 select-none">+1</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="555 123 4567"
                  maxLength={10}
                  className="flex-1 px-4 py-3 text-sm focus:outline-none bg-white"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition disabled:opacity-60"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={verifyOtp} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Enter OTP</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6-digit code"
                className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition disabled:opacity-60"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); }}
                className="px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium"
              >
                Back
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center">Enter the 6-digit code sent to your phone</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignInUser;
